/**
 * Montgomery College (Maryland) brand colors — W52 Worker M, S3.
 *
 * Source: Montgomery College Brand Guidelines PDF (created 2025-11-24), the OWNER-SUPPLIED
 * copy (sha256 ba0222be…6298; extract in
 * admin_tools/backend/data/onboarding/montgomery-college/probes/brand_guide_color_pages.txt):
 *   Core:    MC Purple Pantone 2617 #51237F (RGB 81 35 127), MC Blue Pantone 639 #0095C8
 *   Accents: Aspire Gold Pantone 7549 #FBA93E ("Primary Accent"), Welcome Orange #F15922,
 *            Edge Green #00AC9B, Reflection Blue #51C9E7, Future Fuchsia #B82A91,
 *            Momentum Red #F92329.
 *   Rules:   "Tints of MC Purple and MC Blue may be used sparingly to create lighter
 *            background fields" — and — "Tints or shades are not permitted for accent
 *            colors. They must always appear at 100% opacity."
 * Second first-party source (catalog theme CSS, gateway-styles.css) agrees on the purple
 * (#51237f, 69 uses) and uses a different blue (#0c7bc0); the guide is the authority.
 *
 * Contrast (WCAG, measured — onboarding theme_contrast.json; 12 px text needs 4.5:1):
 *   MC Purple   on white 11.07, warm paper 9.84, sunken 9.00; on #161412 1.66.
 *               White text on it 11.07, so `-strong` is the base brand color.
 *   Accent token = Future Fuchsia #B82A91 at 100% (OWNER RULING, decisions rev 2):
 *               white 5.56, paper 4.94, sunken 4.52 (all AA); #161412 3.31, #1F1C19 3.05
 *               (the 3:1 floor — the theme system has no dark-mode accent token, doc 51 S3-6;
 *               no single hue clears 4.5:1 on both #FFFFFF and #161412).
 *   Aspire Gold #FBA93E fails as TEXT on every light surface (1.57–1.94), and the guide
 *               forbids darkening it, so it is NOT the accent token; it appears at 100% only
 *               on the brand mark (non-text use, owner ruling).
 *
 * `text-accent` renders at 12 px in the audit (override pill, override control, "via
 * override" progress label), which is why the 4.5:1 floor decides the accent.
 *
 * Ladders are HSL interpolations around the brand values (theme_ladders.py), pinned at
 * MC Purple = 700 and Future Fuchsia = 600: brand-aligned for UI states, not an officially
 * published extended palette. The guide allows tints of the core purple; the accent ladder
 * is a UI necessity the owner has acknowledged (accent tints are otherwise not permitted).
 *
 * Brand mark (components/ui/BrandMark.tsx): FRONT face Aspire Gold #FBA93E at 100% in both
 * appearances (no tint/shade of an accent); the shading faces are MC Purple and purple
 * tints. Cross-theme front-face distance (>= 50 required): light — umd 114.2, sjsu 282.9,
 * ucmerced 283.2, sdsu 168.0, deanza 223.1, merced-college 87.5; dark — umd 77.5,
 * sjsu 259.9, ucmerced 308.0, sdsu 151.3, deanza 201.1, merced-college 63.3 (closest, above the 50 floor — passes; driver-reviewed 2026-10-12: both
 * colleges have gold marks; Merced College's dark gold is #F5B400). Logo policy mirrors the
 * other themes: text-only branding, no logo asset embedded.
 */
import type { ThemeColors } from '../types';

// The text-accent hue — the ONE value to change if the accent ruling changes
// (decisions rev 2: owner chose Future Fuchsia at 100% over Aspire Gold for text).
const ACCENT = '184 42 145'; // Future Fuchsia #B82A91

// Aspire Gold at 100% — brand mark front face only (non-text use).
const ASPIRE_GOLD = '251 169 62'; // #FBA93E

export const montgomeryCollegeColors: ThemeColors = {
  // Primary — MC Purple (Pantone 2617, #51237F)
  '--color-primary': '81 35 127',
  // -strong: white text passes at 11.07:1, so -strong is the base brand color.
  '--color-primary-strong': '81 35 127',
  '--color-primary-50': '245 239 251',
  '--color-primary-100': '235 223 246',
  '--color-primary-200': '214 191 237',
  '--color-primary-300': '189 151 226',
  '--color-primary-400': '161 107 214',
  '--color-primary-500': '133 63 202',
  '--color-primary-600': '107 46 168',
  '--color-primary-700': '81 35 127',
  '--color-primary-800': '61 26 96',
  '--color-primary-900': '43 19 68',

  // Accent — Future Fuchsia (#B82A91) at 100% for text; see header.
  '--color-accent': ACCENT,
  '--color-accent-50': '251 238 248',
  '--color-accent-100': '247 222 240',
  '--color-accent-200': '240 189 226',
  '--color-accent-300': '230 147 207',
  '--color-accent-400': '220 101 187',
  '--color-accent-500': '210 56 167',
  '--color-accent-600': ACCENT,
  '--color-accent-700': '133 30 105',
  '--color-accent-800': '100 23 79',
  '--color-accent-900': '71 16 56',

  // Brand-mark faces — Aspire Gold front at 100% (both appearances); purple shading faces.
  '--color-mark-front': ASPIRE_GOLD,
  '--color-mark-top': '81 35 127',
  '--color-mark-side': '61 26 96',
  '--color-mark-front-dm': ASPIRE_GOLD,
  '--color-mark-top-dm': '168 145 191',
  '--color-mark-side-dm': '133 101 165',
};
